import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { formatDistanceToNow, format } from 'date-fns';
import { Mail, MailOpen, Send, Trash2, Inbox, CheckCircle, XCircle } from 'lucide-react';
import { useAdminHub } from '../hooks/useSignalR';
import { RefreshButton } from '../components/RefreshButton';
import { useRefresh } from '../hooks/useRefresh';
import {
  SupportEmailStatus, SupportEmailReplyStatus,
  type SupportEmailSummary, type SupportEmailDetail, type MailboxListResponse,
} from '../types/mailbox';

const DEMO_TOKEN = import.meta.env.VITE_DEMO_TOKEN ?? 'demo-token';
const authHeaders = { Authorization: `Bearer ${DEMO_TOKEN}` };
const api = axios.create({ headers: authHeaders });

// ─────────────────────────────────────────────────────────────────────────────
//  STATUS helpers
// ─────────────────────────────────────────────────────────────────────────────
const STATUS_LABELS: Record<SupportEmailStatus, string> = {
  [SupportEmailStatus.Unread]:    'Unread',
  [SupportEmailStatus.Read]:      'Read',
  [SupportEmailStatus.Replied]:   'Draft',
  [SupportEmailStatus.Sent]:      'Sent',
  [SupportEmailStatus.Dismissed]: 'Dismissed',
};

const STATUS_STYLE: Record<SupportEmailStatus, React.CSSProperties> = {
  [SupportEmailStatus.Unread]:    { background: '#d1fae5', color: '#065f46' },
  [SupportEmailStatus.Read]:      { background: '#f3f4f6', color: '#6b7280' },
  [SupportEmailStatus.Replied]:   { background: '#fef3c7', color: '#92400e' },
  [SupportEmailStatus.Sent]:      { background: '#dcfce7', color: '#166534' },
  [SupportEmailStatus.Dismissed]: { background: '#fee2e2', color: '#991b1b' },
};

function StatusBadge({ status }: { status: SupportEmailStatus }) {
  return (
    <span style={{ ...s.badge, ...STATUS_STYLE[status] }}>
      {STATUS_LABELS[status]}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  EMAIL LIST
// ─────────────────────────────────────────────────────────────────────────────
type FilterTab = 'all' | SupportEmailStatus;

interface EmailListProps {
  emails:        SupportEmailSummary[];
  selectedId:    string | null;
  loading:       boolean;
  filter:        FilterTab;
  onFilter:      (f: FilterTab) => void;
  onSelect:      (id: string) => void;
  onRefresh:     () => void;
  isRefreshing:  boolean;
  lastRefreshed: Date | null;
}

function EmailList({ emails, selectedId, loading, filter, onFilter, onSelect, onRefresh, isRefreshing, lastRefreshed }: EmailListProps) {
  const counts = {
    all:      emails.length,
    unread:   emails.filter(e => e.status === SupportEmailStatus.Unread).length,
    replied:  emails.filter(e => e.status === SupportEmailStatus.Replied).length,
  };

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all',                          label: `All (${counts.all})` },
    { key: SupportEmailStatus.Unread,      label: `Unread (${counts.unread})` },
    { key: SupportEmailStatus.Read,        label: 'Read' },
    { key: SupportEmailStatus.Replied,     label: `Draft (${counts.replied})` },
    { key: SupportEmailStatus.Sent,        label: 'Sent' },
    { key: SupportEmailStatus.Dismissed,   label: 'Dismissed' },
  ];

  return (
    <div style={s.listPanel}>
      {/* Header */}
      <div style={s.listHeader}>
        <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Inbox</span>
        <RefreshButton onRefresh={onRefresh} isRefreshing={isRefreshing} lastRefreshed={lastRefreshed} />
      </div>

      {/* Filter tabs */}
      <div style={s.filterTabs}>
        {tabs.map(t => (
          <button
            key={String(t.key)}
            style={{ ...s.filterTab, ...(filter === t.key ? s.filterTabActive : {}) }}
            onClick={() => onFilter(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div style={s.listScroll}>
        {loading && (
          <div style={s.centreState}>
            <div style={s.spinner} />
          </div>
        )}
        {!loading && emails.length === 0 && (
          <div style={s.centreState}>
            <Inbox size={32} color="#d1d5db" />
            <span style={{ color: '#9ca3af', fontSize: 13, marginTop: 8 }}>No emails in this folder</span>
          </div>
        )}
        {!loading && emails.map(email => (
          <EmailRow
            key={email.id}
            email={email}
            selected={selectedId === email.id}
            onClick={() => onSelect(email.id)}
          />
        ))}
      </div>
    </div>
  );
}

function EmailRow({ email, selected, onClick }: {
  email: SupportEmailSummary; selected: boolean; onClick: () => void;
}) {
  const isUnread = email.status === SupportEmailStatus.Unread;
  return (
    <div
      onClick={onClick}
      style={{
        ...s.emailRow,
        ...(selected ? s.emailRowSelected : {}),
        fontWeight: isUnread ? 600 : 400,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
        <span style={{ fontSize: 13, color: '#111827', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {isUnread ? <Mail size={12} style={{ display: 'inline', marginRight: 4, color: '#047857' }} /> : null}
          {email.fromName || email.fromAddress}
        </span>
        <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {formatDistanceToNow(new Date(email.receivedAt), { addSuffix: true })}
        </span>
      </div>
      <div style={{ fontSize: 12, color: '#374151', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {email.subject}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
        <span style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {email.bodyText?.slice(0, 80)}
        </span>
        <StatusBadge status={email.status} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  EMAIL DETAIL
// ─────────────────────────────────────────────────────────────────────────────
interface EmailDetailProps {
  emailId:        string | null;
  onStatusChange: (id: string, status: SupportEmailStatus) => void;
}

function EmailDetail({ emailId, onStatusChange }: EmailDetailProps) {
  const [detail,        setDetail]        = useState<SupportEmailDetail | null>(null);
  const [loading,       setLoading]       = useState(false);
  const [replySubject,  setReplySubject]  = useState('');
  const [replyBody,     setReplyBody]     = useState('');
  const [saving,        setSaving]        = useState(false);
  const [sending,       setSending]       = useState(false);
  const [draftSaved,    setDraftSaved]    = useState(false);
  const [confirmSend,   setConfirmSend]   = useState(false);
  const [confirmDismiss,setConfirmDismiss]= useState(false);
  const [error,         setError]         = useState('');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!emailId) { setDetail(null); return; }
    setLoading(true);
    setError('');
    setDraftSaved(false);
    setConfirmSend(false);
    setConfirmDismiss(false);

    api.get<SupportEmailDetail>(`/api/v1/support/mailbox/${emailId}`)
      .then(r => {
        setDetail(r.data);
        const d = r.data;
        setReplySubject(d.reply?.subject ?? `Re: ${d.subject}`);
        setReplyBody(d.reply?.bodyHtml ?? '');
        if (d.status === SupportEmailStatus.Unread)
          onStatusChange(emailId, SupportEmailStatus.Read);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [emailId]);

  const saveDraft = async () => {
    if (!detail) return;
    setSaving(true); setError(''); setDraftSaved(false);
    try {
      await api.post(`/api/v1/support/mailbox/${detail.id}/reply`, {
        subject: replySubject,
        bodyHtml: replyBody,
      });
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 2500);
      onStatusChange(detail.id, SupportEmailStatus.Replied);
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const sendReply = async () => {
    if (!detail) return;
    setSending(true); setError('');
    try {
      await api.post(`/api/v1/support/mailbox/${detail.id}/reply`, {
        subject: replySubject,
        bodyHtml: replyBody,
      });
      await api.post(`/api/v1/support/mailbox/${detail.id}/send`);
      onStatusChange(detail.id, SupportEmailStatus.Sent);
      setDetail(d => d ? { ...d, status: SupportEmailStatus.Sent } : d);
      setConfirmSend(false);
    } catch (e: any) { setError(e.message); }
    finally { setSending(false); }
  };

  const dismiss = async () => {
    if (!detail) return;
    try {
      await api.delete(`/api/v1/support/mailbox/${detail.id}`);
      onStatusChange(detail.id, SupportEmailStatus.Dismissed);
      setDetail(d => d ? { ...d, status: SupportEmailStatus.Dismissed } : d);
      setConfirmDismiss(false);
    } catch (e: any) { setError(e.message); }
  };

  if (!emailId) {
    return (
      <div style={{ ...s.detailPanel, ...s.centreState }}>
        <MailOpen size={48} color="#d1d5db" />
        <span style={{ color: '#9ca3af', fontSize: 14, marginTop: 12 }}>Select an email to read</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ ...s.detailPanel, ...s.centreState }}>
        <div style={s.spinner} />
      </div>
    );
  }

  if (!detail) {
    return (
      <div style={{ ...s.detailPanel, ...s.centreState }}>
        <XCircle size={32} color="#f87171" />
        <span style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{error || 'Email not found'}</span>
      </div>
    );
  }

  const isSent      = detail.status === SupportEmailStatus.Sent;
  const isDismissed = detail.status === SupportEmailStatus.Dismissed;
  const canReply    = !isSent && !isDismissed;

  return (
    <div style={s.detailPanel}>
      <div style={s.detailScroll}>

        {/* ── Header ── */}
        <div style={s.detailHeader}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>
              {detail.subject}
            </h2>
            <div style={{ marginTop: 4, fontSize: 13, color: '#6b7280' }}>
              <strong style={{ color: '#374151' }}>
                {detail.fromName ? `${detail.fromName} ` : ''}
              </strong>
              &lt;{detail.fromAddress}&gt;
              <span style={{ marginLeft: 12 }}>
                {format(new Date(detail.receivedAt), 'dd MMM yyyy, HH:mm')}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            <StatusBadge status={detail.status} />
            {!isDismissed && !isSent && (
              <button
                style={{ ...s.btnDanger }}
                onClick={() => setConfirmDismiss(true)}
                title="Dismiss"
              >
                <Trash2 size={14} style={{ marginRight: 4 }} />
                Dismiss
              </button>
            )}
          </div>
        </div>

        {/* ── Body ── */}
        <div style={s.emailBody}>
          {detail.bodyHtml ? (
            <iframe
              ref={iframeRef}
              srcDoc={detail.bodyHtml}
              sandbox="allow-same-origin"
              style={{ width: '100%', minHeight: 280, border: 'none', display: 'block' }}
              title="Email body"
              onLoad={() => {
                if (iframeRef.current?.contentDocument?.body) {
                  const h = iframeRef.current.contentDocument.body.scrollHeight;
                  iframeRef.current.style.height = `${Math.max(h + 24, 120)}px`;
                }
              }}
            />
          ) : (
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 13, color: '#374151' }}>
              {detail.bodyText || '(no content)'}
            </pre>
          )}
        </div>

        {/* ── Sent banner ── */}
        {isSent && detail.reply && (
          <div style={s.sentBanner}>
            <CheckCircle size={16} style={{ flexShrink: 0 }} />
            <span>
              Reply sent on {format(new Date(detail.reply.sentAt!), 'dd MMM yyyy, HH:mm')}
            </span>
          </div>
        )}

        {/* ── Reply panel ── */}
        {canReply && (
          <div style={s.replyPanel}>
            <div style={s.replyTitle}>
              {detail.reply ? '✏️  Edit Draft Reply' : '↩️  Write Reply'}
            </div>

            <label style={s.fieldLabel}>Subject</label>
            <input
              style={s.input}
              value={replySubject}
              onChange={e => setReplySubject(e.target.value)}
              placeholder="Re: ..."
            />

            <label style={s.fieldLabel}>
              Body
              <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>
                (HTML supported)
              </span>
            </label>
            <textarea
              style={s.textarea}
              value={replyBody}
              onChange={e => setReplyBody(e.target.value)}
              placeholder="Type your reply here…"
              rows={8}
            />

            {error && <div style={s.errorMsg}>{error}</div>}

            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                style={{ ...s.btnSecondary, opacity: saving ? 0.6 : 1 }}
                onClick={saveDraft}
                disabled={saving}
              >
                {saving ? <SpinnerInline /> : null}
                Save Draft
              </button>
              <button
                style={{ ...s.btnPrimary, opacity: sending ? 0.6 : 1 }}
                onClick={() => setConfirmSend(true)}
                disabled={sending || !replyBody.trim()}
              >
                <Send size={13} style={{ marginRight: 4 }} />
                Send Reply
              </button>
              {draftSaved && (
                <span style={{ fontSize: 13, color: '#166534', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <CheckCircle size={14} /> Draft saved
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Confirm send dialog ── */}
        {confirmSend && (
          <ConfirmDialog
            message={`Send reply to ${detail.fromAddress}? This cannot be undone.`}
            confirmLabel={sending ? 'Sending…' : 'Send'}
            confirmDisabled={sending}
            onConfirm={sendReply}
            onCancel={() => setConfirmSend(false)}
          />
        )}

        {/* ── Confirm dismiss dialog ── */}
        {confirmDismiss && (
          <ConfirmDialog
            message="Dismiss this email without replying?"
            confirmLabel="Dismiss"
            confirmStyle={{ background: '#ef4444' }}
            onConfirm={dismiss}
            onCancel={() => setConfirmDismiss(false)}
          />
        )}

      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  SHARED SMALL COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
function SpinnerInline() {
  return (
    <span style={{
      display: 'inline-block', width: 12, height: 12, border: '2px solid currentColor',
      borderTopColor: 'transparent', borderRadius: '50%',
      animation: 'spin 0.6s linear infinite', marginRight: 6,
    }} />
  );
}

function ConfirmDialog({ message, confirmLabel, confirmDisabled, confirmStyle, onConfirm, onCancel }: {
  message:         string;
  confirmLabel:    string;
  confirmDisabled?: boolean;
  confirmStyle?:   React.CSSProperties;
  onConfirm:       () => void;
  onCancel:        () => void;
}) {
  return (
    <div style={s.confirmOverlay}>
      <div style={s.confirmBox}>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: '#111827' }}>{message}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button style={s.btnSecondary} onClick={onCancel}>Cancel</button>
          <button
            style={{ ...s.btnPrimary, ...confirmStyle, opacity: confirmDisabled ? 0.6 : 1 }}
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function EmailMonitoringPage() {
  const [emails,     setEmails]     = useState<SupportEmailSummary[]>([]);
  const [allEmails,  setAllEmails]  = useState<SupportEmailSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter,     setFilter]     = useState<FilterTab>('all');
  const [loading,    setLoading]    = useState(false);
  const [unreadCount,setUnreadCount]= useState(0);

  const fetchEmails = useCallback(async (statusFilter?: SupportEmailStatus) => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page: 1, pageSize: 100 };
      if (statusFilter !== undefined) params.status = statusFilter;
      const res = await api.get<MailboxListResponse>('/api/v1/support/mailbox', { params });
      const items = res.data.items ?? [];
      setAllEmails(items);
      setEmails(items);
      setUnreadCount(items.filter(e => e.status === SupportEmailStatus.Unread).length);
    } catch { /* silent - no crash on poll failure */ }
    finally { setLoading(false); }
  }, []);

  const { isRefreshing, lastRefreshed, refresh } = useRefresh(fetchEmails);

  // Initial load
  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  // Apply local filter
  useEffect(() => {
    if (filter === 'all') {
      setEmails(allEmails);
    } else {
      setEmails(allEmails.filter(e => e.status === filter));
    }
  }, [filter, allEmails]);

  // SignalR: new emails arrive in real-time
  useAdminHub({
    getToken: () => Promise.resolve(DEMO_TOKEN),
    onNewSupportEmail: (email: SupportEmailSummary) => {
      setAllEmails(prev => {
        if (prev.some(e => e.id === email.id)) return prev;
        return [email, ...prev];
      });
      setUnreadCount(c => c + 1);
    },
  });

  const handleStatusChange = (id: string, newStatus: SupportEmailStatus) => {
    setAllEmails(prev =>
      prev.map(e => e.id === id ? { ...e, status: newStatus } : e)
    );
    if (newStatus === SupportEmailStatus.Read && allEmails.find(e => e.id === id)?.status === SupportEmailStatus.Unread) {
      setUnreadCount(c => Math.max(0, c - 1));
    }
  };

  return (
    <div style={s.page}>
      {/* ── Page header ── */}
      <div style={s.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Mail size={20} color="#047857" />
          <h1 style={s.h1}>Email Monitoring</h1>
          {unreadCount > 0 && (
            <span style={s.unreadBadge}>{unreadCount} new</span>
          )}
        </div>
        <span style={s.subLabel}>Support Mailbox — review, reply and send</span>
      </div>

      {/* ── Two-panel layout ── */}
      <div style={s.twoPanel}>
        <EmailList
          emails={emails}
          selectedId={selectedId}
          loading={loading}
          filter={filter}
          onFilter={setFilter}
          onSelect={setSelectedId}
          onRefresh={refresh}
          isRefreshing={isRefreshing}
          lastRefreshed={lastRefreshed}
        />
        <EmailDetail
          emailId={selectedId}
          onStatusChange={handleStatusChange}
        />
      </div>

      {/* Keyframe for spinner (injected once) */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────────────────────────────────────
const s: Record<string, React.CSSProperties> = {
  page:           { width: '100%', padding: '20px 16px', height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column' },
  pageHeader:     { marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 },
  h1:             { margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' },
  subLabel:       { fontSize: 13, color: '#6b7280' },
  unreadBadge:    { fontSize: 12, padding: '2px 10px', background: '#047857', color: '#fff', borderRadius: 12, fontWeight: 600 },

  twoPanel:       { display: 'flex', flex: 1, gap: 0, border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff', minHeight: 0 },

  // List panel
  listPanel:      { width: 340, minWidth: 260, borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', background: '#fafafa' },
  listHeader:     { padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' },
  filterTabs:     { display: 'flex', flexWrap: 'wrap', gap: 2, padding: '6px 8px', borderBottom: '1px solid #e5e7eb' },
  filterTab:      { padding: '4px 10px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: '#6b7280', borderRadius: 6, whiteSpace: 'nowrap' },
  filterTabActive:{ background: '#ecfdf5', color: '#047857', fontWeight: 600 },
  listScroll:     { flex: 1, overflowY: 'auto' },

  emailRow:       { padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', transition: 'background 0.1s' },
  emailRowSelected:{ background: '#ecfdf5', borderLeft: '3px solid #10b981' },

  // Detail panel
  detailPanel:    { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  detailScroll:   { flex: 1, overflowY: 'auto', padding: '20px 24px' },
  detailHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid #e5e7eb' },
  emailBody:      { background: '#f9fafb', borderRadius: 8, padding: 16, marginBottom: 20, border: '1px solid #e5e7eb', overflowX: 'auto' },

  replyPanel:     { borderTop: '2px solid #e5e7eb', paddingTop: 18, marginTop: 4 },
  replyTitle:     { fontWeight: 600, fontSize: 14, color: '#374151', marginBottom: 12 },
  fieldLabel:     { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, marginTop: 10 },
  input:          { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
  textarea:       { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' },

  sentBanner:     { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#dcfce7', color: '#166534', borderRadius: 8, fontSize: 13, marginBottom: 16 },
  errorMsg:       { background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '7px 12px', color: '#dc2626', fontSize: 12, marginTop: 8 },

  // Confirm dialog
  confirmOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  confirmBox:     { background: '#fff', borderRadius: 10, padding: 24, maxWidth: 380, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' },

  // Buttons
  btnPrimary:     { padding: '8px 16px', background: '#047857', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', fontWeight: 500 },
  btnSecondary:   { padding: '8px 14px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', fontWeight: 500 },
  btnDanger:      { padding: '6px 12px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 7, fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', fontWeight: 500 },
  iconBtn:        { padding: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center', borderRadius: 4 },

  // Generic
  badge:          { fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600, whiteSpace: 'nowrap' },
  centreState:    { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 },
  spinner:        { width: 28, height: 28, border: '3px solid #e5e7eb', borderTopColor: '#10b981', borderRadius: '50%', animation: 'spin 0.7s linear infinite' },
};
